import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import { Field, FieldProps, Form, Formik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { RouterLink } from '../../../../components/Link';
import { TextField } from '../../../../components/TextField';
import type { OnSubmit } from '../../../../design-system';
import { createSchema } from '../../../../utils/formUtils';

type SignInFormFields = { email: string; password: string };

const initialValues: SignInFormFields = {
  email: '',
  password: '',
};

const validationSchema = createSchema<SignInFormFields>({
  email: Yup.string().email('Email must be a valid email address').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

export interface SignInFormProps {
  onSubmit: OnSubmit<SignInFormFields>;
}

export const SignInForm: React.FC<SignInFormProps> = ({ onSubmit }) => (
  <Fade in>
    <div>
      <Formik<SignInFormFields>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ errors, isSubmitting }) => (
          <Form>
            <Box>
              <Field name="email">
                {({ field: { onBlur, onChange, name, value } }: FieldProps<SignInFormFields['email']>) => (
                  <TextField
                    autoFocus
                    disabled={isSubmitting}
                    error={!!errors.email}
                    fullWidth
                    helperText={errors.email}
                    label="Email"
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    size="medium"
                    type="email"
                    value={value}
                  />
                )}
              </Field>
            </Box>
            <Box mt={2}>
              <Field name="password">
                {({ field: { onBlur, onChange, name, value } }: FieldProps<SignInFormFields['password']>) => (
                  <TextField
                    disabled={isSubmitting}
                    error={!!errors.password}
                    fullWidth
                    helperText={errors.password}
                    label="Password"
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    size="medium"
                    type="password"
                    value={value}
                  />
                )}
              </Field>
            </Box>
            <Box mt={3}>
              <Button color="primary" disabled={isSubmitting} fullWidth size="large" type="submit" variant="contained">
                {isSubmitting ? <CircularProgress size={26} color="inherit" /> : 'Sign in'}
              </Button>
            </Box>
            <Box mt={2} textAlign="center">
              <RouterLink to="/auth/forgot-password" fontWeight="bold" color="primary">
                Forgot password?
              </RouterLink>
            </Box>
          </Form>
        )}
      </Formik>
    </div>
  </Fade>
);
